import axios from 'axios';
import { toast } from 'react-toastify';
import { getDeviceId } from './deviceFingerprint';
import { recordRequest } from './requestGovernor';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 240000,
});

// Add token + device fingerprint to every request
api.interceptors.request.use(
  async (config) => {
    // Every outgoing request counts against the client-side budget that
    // polling loops self-throttle against — see requestGovernor.js.
    recordRequest();
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    try {
      const deviceId = await getDeviceId();
      if (deviceId) config.headers['X-Device-Id'] = deviceId;
    } catch {
      // fingerprint failure is non-fatal
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Prevent duplicate session-replaced handling across concurrent requests
let _sessionReplacedFired = false;

// Dedupe window for the 429 toast below — a burst of concurrent requests
// hitting the same limit should show one toast, not one per request.
let _last429ToastAt = 0;

// Handle responses and errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const isAuthEndpoint = error.config?.url?.includes('/auth/login') ||
                           error.config?.url?.includes('/auth/register');

    if (status === 401 && !isAuthEndpoint) {
      const code = error.response?.data?.code;

      if (code === 'SESSION_REPLACED' && !_sessionReplacedFired) {
        _sessionReplacedFired = true;
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        // Let SessionWatchdog show the modal; it will redirect to /login after countdown
        window.dispatchEvent(new CustomEvent('session-replaced'));
        return Promise.reject(error);
      }

      if (!_sessionReplacedFired) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
      }
    }

    // IP whitelist block — fire a global event so the app can show a blocking screen.
    // Skip for the login endpoint so the login form can display the error itself.
    if (status === 403 && error.response?.data?.code === 'IP_NOT_WHITELISTED' && !isAuthEndpoint) {
      window.dispatchEvent(
        new CustomEvent('ip-blocked', { detail: { message: error.response.data.message } })
      );
    }

    // 429s (the app-wide rate limiter, or a user's daily AI budget cap) get a
    // guaranteed toast here, on top of whatever each call site's own catch
    // block does — some call sites are fire-and-forget with no catch at all,
    // which previously meant a 429 there was completely silent. Deduped so a
    // burst of parallel requests tripping the same limit doesn't stack five
    // identical toasts.
    //
    // Exception: requests explicitly marked `silent` (background heartbeats/
    // polling the user never asked for — see SessionWatchdog's /auth/ping,
    // Automation.jsx's SSE-triggered refetch and fallback poll) never show
    // this toast even if they do get rate-limited. The user has no action to
    // take in response to a background check failing, and alarming them
    // about "making requests too quickly" when they haven't clicked anything
    // is confusing, not helpful — confirmed as a real report from production.
    if (status === 429 && !error.config?.silent) {
      const now = Date.now();
      if (now - _last429ToastAt > 4000) {
        _last429ToastAt = now;
        toast.error(error.response?.data?.message || 'Too many requests — please wait a moment and try again.');
      }
    }

    return Promise.reject(error);
  }
);

export default api;
